import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import { CausedByChip } from './CausedByChip';
import { CopyableText } from './CopyableText';
import { EventTypeIndicator } from './EventTypeIndicator';
import { AiBadge } from './AiBadge';
import { formatTimestamp, getStoredTimeFormat } from '../utils/timeFormat';
import { resolveEventType } from '../utils/resolveEventType';
import { isBedrockEvent } from '../utils/isBedrockEvent';

interface Props {
  event: Record<string, unknown>;
  storeName: string;
  onClose: () => void;
  onSelectCausedBy?: (eventId: string) => void;
}

export function EventDetailPanel({ event, storeName, onClose, onSelectCausedBy }: Props) {
  const navigate = useNavigate();
  const metadata = (event?.metadata as Record<string, unknown>) || {};
  const data = event?.data || {};
  const displayType = resolveEventType({
    metadata: metadata as { eventType?: string },
    data,
  });

  // Cloudscape Link renders a real <a href> only when href is set. Passing
  // both href AND onFollow gives us "Open in new tab" via right-click while
  // still doing SPA navigation on plain click.
  const schemaHref = displayType !== '—' ? `/stores/${storeName}/schemas/${encodeURIComponent(displayType)}` : undefined;
  const domainHref = metadata.domain ? `/stores/${metadata.domain}` : undefined;
  const lineageHref = typeof metadata.eventId === 'string' && metadata.eventId
    ? `/stores/${storeName}/lineage/${metadata.eventId}`
    : undefined;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h3"
        actions={<Button variant="icon" iconName="close" onClick={onClose} />}
      >
        {schemaHref ? (
          <Link
            href={schemaHref}
            fontSize="heading-s"
            onFollow={(e) => { e.preventDefault(); navigate(schemaHref); }}
          >
            <EventTypeIndicator eventType={displayType} />
          </Link>
        ) : (
          <EventTypeIndicator eventType={displayType} />
        )}
        {isBedrockEvent({ metadata: metadata as { source?: string }, data }) && <AiBadge />}
      </Header>

      <Container header={<Header variant="h3">Metadata</Header>}>
        <ColumnLayout columns={1} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Event ID</Box>
            {metadata.eventId ? <CopyableText text={metadata.eventId as string} /> : <Box>—</Box>}
          </div>
          <div>
            <Box variant="awsui-key-label">Event type</Box>
            {schemaHref ? (
              <Link
                href={schemaHref}
                onFollow={(e) => { e.preventDefault(); navigate(schemaHref); }}
              >
                {displayType}
              </Link>
            ) : (
              <Box>—</Box>
            )}
          </div>
          <div>
            <Box variant="awsui-key-label">Version</Box>
            <Box>{metadata.version !== undefined ? String(metadata.version) : (metadata.sequence !== undefined ? String(metadata.sequence) : '—')}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Timestamp</Box>
            <Box>{formatTimestamp(metadata.timestamp as string | undefined, getStoredTimeFormat())}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Source</Box>
            <Box>{(metadata.source as string) || '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Domain</Box>
            {domainHref ? (
              <Link
                href={domainHref}
                onFollow={(e) => { e.preventDefault(); navigate(domainHref); }}
              >
                {metadata.domain as string}
              </Link>
            ) : <Box>—</Box>}
          </div>
          <div>
            <Box variant="awsui-key-label">Schema version</Box>
            <Box>{metadata.schemaVersion !== undefined ? String(metadata.schemaVersion) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Correlation ID</Box>
            <Box>{(metadata.correlationId as string) || '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Caused by</Box>
            <CausedByChip
              causedBy={metadata.causedBy as string | string[] | null | undefined}
              storeName={storeName}
              onSelect={onSelectCausedBy}
            />
          </div>
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h3">Data</Header>}>
        <pre style={{ fontFamily: 'monospace', fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Container>

      {lineageHref ? (
        <Button
          variant="normal"
          href={lineageHref}
          onFollow={(e) => { e.preventDefault(); navigate(lineageHref); }}
        >
          View Lineage
        </Button>
      ) : null}
    </SpaceBetween>
  );
}
